// DOM fallback variant of gallery-animate-zoom.js for iOS.
//
// Why this exists: WebGL on iOS Safari (and iOS Chrome, which uses WebKit)
// kills tabs flagged as "unresponsive" under sustained gallery interaction
// even when memory is tiny. Round 1–5 of mitigation in the Pixi gallery
// hit this wall. iOS handles DOM <img> rendering far better — decode-on-
// demand, drop offscreen, no WebGL pressure — so we route iOS users here.
//
// Scope: matches the *public* surface of GalleryAnimateZoom (init, destroy,
// setFilter, navigateTo, openLightbox, handleClick, etc.) so app.js wires
// the same way. Sticky-rows and the spotlight gap mirror the Pixi version
// (the world view rebases the gap's ramp — see its _groupOffset).
//
// Pan/zoom uses the same pointer-input.js helpers as the Pixi version.

import PhotoSwipe from 'photoswipe';
import { decode as bhDecode } from 'blurhash';
import { renderFilterButtons } from './filter-ui.js';
import { resetCrumbTracking } from './gallery-breadcrumb.js';
import { bindPointerEvents, applyInertia, stepZoom } from './pointer-input.js';
import { showShare, hideShare } from './share.js';

const ITEM_W      = 220;
const ITEM_H      = 330;
const GAP         = 0;
const ROW_GAP     = 20;
const SECTION_GAP = ITEM_H * 1.5;

// ── Tunable zoom range (mobile only — this file is the mobile path) ──────────
// Both ends matter for memory pressure on iOS. Adjust either freely; values
// below are conservative defaults.
//   ZOOM_MIN — too low and many items pile into the viewport simultaneously
//              and the image-decode pipeline chokes (we already saw this).
//   ZOOM_MAX — too high and items render very large; browser keeps the tier-2
//              decoded data (3.84 MB per item) around for items the user is
//              actively viewing. A lower max means tier-2 still loads but
//              fewer items can be at it at once.
const ZOOM_MIN      = 0.25;
const ZOOM_MAX      = 1.7;     // was 4; tuneable
const ZOOM_LIGHTBOX = 1.8;   // click at zoom ≥ this opens lightbox instead of zooming
const CLAMP_MIN_PX  = 60;

// Sticky-row config (mirrors PixiJS variant). Past STICKY_AT × fitZ the gallery
// hard-locks panY to the nearest row's centre; between STICKY_AT × STICKY_SOFT
// and STICKY_AT it soft-snaps toward it; below that, panning is free.
// Keep STICKY_AT / STICKY_SOFT in step with gallery-animate-zoom.js, where the
// reasoning for the values lives.
const STICKY_ROWS     = true;
const STICKY_STRENGTH = 0.25;
const STICKY_ESCAPE   = 0.15;
const STICKY_AT       = 6;
const STICKY_SOFT     = 0.8;

// Spotlight-gap config (mirrors PixiJS variant). The row nearest the screen
// centre gets ROW_GAP_FOCUS world-units of breathing room (split half above,
// half below). All other rows fade smoothly via a sigmoid centred on the
// screen's world-y. Engages between fitZ and labelFullZ.
export const ROW_GAP_FOCUS    = 460;
export const ROW_GAP_SOFTNESS = 200;

// Cap on concurrent tier-upgrade preloads. Without this, every zoom across a
// tier boundary kicks off ~500 Image() preloads at once and iOS Safari's
// decode pipeline chokes (the original crash mode). Visible items go first.
const TIER_LOAD_MAX = 3;

// At or below this item count the gallery runs as a "keep-all" filmstrip: every
// item stays mounted (never unmounted) so scrolling back never reloads. The
// single-collection mobile view (~25 looks) is always under this; a rare larger
// set falls back to the windowed mount/unmount path.
const KEEP_ALL_MAX = 60;

const TIERS = ['60x0', '300x0', '800x0'];

function tierForZoom(zoom) {
    const px = ITEM_W * zoom;
    if (px < 80)  return 0;
    if (px < 200) return 1;
    return 2;
}

class GalleryAnimateZoomDOM {
    constructor() {
        this.viewport = document.getElementById('gallery-viewport');
        if (!this.viewport) return;

        this.data    = [];
        this.collMap = new Map();
        this.items   = [];
        this.groups  = [];
        this.worldW  = 0;
        this.worldH  = 0;

        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.velX = 0;
        this.velY = 0;
        this.W    = window.innerWidth;
        this.H    = window.innerHeight;

        this.zoomVel  = 0;
        this.zoomCX   = 0;
        this.zoomCY   = 0;
        this.ZOOM_LEVELS = [];
        this.ZOOM_MIN = ZOOM_MIN;
        this.ZOOM_MAX = ZOOM_MAX;

        this._raf         = null;
        this._dragging    = false;
        this._crumbItem   = null;
        this._crumbInitialised = false;
        this._crumbTrackingStarted = false;
        this._crumbVisible = false;
        this._lastPanKey  = '';
        this._activeFilter = 'all';
        this._activeCollection = null; // mobile shows ONE collection; resolved on first build
        this._collectionList   = [];   // [{key,label}] — full switcher list
        this._lastTier    = -1;
        this.lbIndex      = new Map();

        this._world       = null;
        this._groupEls    = [];

        // Tier-upgrade preload queue + active-count (concurrency cap).
        // Without this, every tier change kicks off ~500 Image() preloads
        // simultaneously and iOS Safari's image-decode pipeline chokes.
        this._tierLoadQueue  = [];
        this._tierLoadActive = 0;
        this._sceneFillTimer = null;

        // Set of items currently mounted in the DOM. Bounded by viewport
        // visibility — see _mountItem / _unmountItem / _updateScene.
        this._mounted = new Set();

        this.init();
    }

    destroy() {
        this._destroyed = true;
        if (this._raf !== null) { cancelAnimationFrame(this._raf); this._raf = null; }
        if (this._sceneFillTimer) { clearTimeout(this._sceneFillTimer); this._sceneFillTimer = null; }
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        // Stop in-flight thumbnail loads so they don't keep downloading after a
        // swup navigation: drop the queue and blank any pending <img> sources.
        this._tierLoadQueue.length = 0;
        for (const item of this._mounted) {
            if (item._imgA) item._imgA.src = '';
            if (item._imgB) item._imgB.src = '';
        }
        if (this._world) {
            this._world.remove();
            this._world = null;
        }
    }

    async init() {
        this._onResize = () => this.onResize();
        window.addEventListener('resize', this._onResize);
        await this.loadData();
        this.buildLayout();
        this.bindEvents();
        this.checkUrlParam();
        this._syncWorld();
        this._wake();
    }

    onResize() {
        this.W = window.innerWidth;
        this.H = window.innerHeight;
        if (this.worldH > 0) {
            this.zoom = Math.max(this.ZOOM_MIN, this.H / this.worldH);
            this._syncWorld();
        }
    }

    async loadData() {
        try {
            const [itemsRes, collRes] = await Promise.all([
                fetch(`${window.JK.pbUrl}/api/collections/items/records?` + new URLSearchParams({
                    perPage: 500,
                    filter:  `type='${this.viewport.dataset.filter || 'look'}'`,
                    fields:  'id,collectionId,title,type,keywords,file,collection,img_blurhash,img_w,img_h',
                    expand:  'collection',
                    sort:    'type,title',
                })),
                fetch(`${window.JK.pbUrl}/api/collections/collections/records?` + new URLSearchParams({
                    perPage: 100,
                    sort:    'order',
                })),
            ]);
            const itemsJson = await itemsRes.json();
            const collJson  = await collRes.json();
            this.data = itemsJson.items || [];
            const natural = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
            this.data.sort((a, b) => natural.compare(a.title || '', b.title || ''));
            this.collMap = new Map((collJson.items || []).map(c => [c.id, c]));
            for (const item of this.data) {
                const ec = item.expand?.collection;
                if (ec && !this.collMap.has(ec.id)) this.collMap.set(ec.id, ec);
            }
        } catch (err) {
            console.error('GalleryAnimateZoomDOM: load failed', err);
        }
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    buildLayout() {
        // Wipe any existing world (Swup re-init or filter rebuild). Items in
        // _mounted have been destroyed along with the world; clear the set.
        if (this._world) { this._world.remove(); this._world = null; }
        this._mounted.clear();
        this._groupEls = [];
        this.items     = [];
        this.groups    = [];
        this.worldW    = 0;

        const nameToId = new Map([...this.collMap.entries()].map(([id, c]) => [c.name || c.title, id]));
        const keyFor = item => {
            let key = item.collection || item.expand?.collection?.id;
            if (!key) {
                const m = (item.title || '').match(/^([A-Z]{2}\d{2})\b/);
                key = (m && nameToId.get(m[1])) || '__none__';
            }
            return key;
        };

        const filter    = this.viewport.dataset.filter || 'look';
        const typeItems = this.data.filter(i => i.type === filter);

        const sections    = this._sectionsForBuild(typeItems, keyFor);
        const isAccessory = filter === 'accessory';

        const world = document.createElement('div');
        world.className = 'gallery-dom-world';
        let y = 0;

        for (const section of sections) {
            if (!section.items.length) continue;

            const byId = new Map();
            for (const item of section.items) {
                const key = keyFor(item);
                if (!byId.has(key)) byId.set(key, []);
                byId.get(key).push(item);
            }
            const orderedKeys = [...this.collMap.keys()].filter(k => byId.has(k));
            if (byId.has('__none__')) orderedKeys.push('__none__');

            for (let gi = 0; gi < orderedKeys.length; gi++) {
                const key  = orderedKeys[gi];
                const coll = this.collMap.get(key);
                const row  = byId.get(key) || [];

                const groupEl = document.createElement('div');
                groupEl.className = 'gallery-dom-group';
                groupEl.style.top = `${y}px`;

                let x = 0, rowH = 0;
                for (const item of row) {
                    if (isAccessory) {
                        item._h  = ITEM_H;
                        const aspect = (item.img_w && item.img_h) ? item.img_w / item.img_h : ITEM_W / ITEM_H;
                        if (aspect > ITEM_W / ITEM_H) { item._dw = ITEM_W; item._dh = ITEM_W / aspect; }
                        else                          { item._dh = ITEM_H; item._dw = ITEM_H * aspect; }
                        item._dx = (ITEM_W - item._dw) / 2;
                        item._dy = (ITEM_H - item._dh) / 2;
                    } else {
                        item._h  = (item.img_w && item.img_h)
                            ? Math.round(ITEM_W * item.img_h / item.img_w)
                            : ITEM_H;
                        item._dw = ITEM_W;
                        item._dh = item._h;
                        item._dx = 0;
                        item._dy = 0;
                    }
                    item._x       = x;
                    item._y       = y;
                    item._collKey = key;
                    item._group   = null; // patched after groups push
                    // <img>/frame are NOT created here — they're created on
                    // demand by _mountItem when the item enters the viewport
                    // and removed by _unmountItem when it leaves. This bounds
                    // decoded image memory to roughly the visible item count.
                    item._el         = null;
                    item._frameEl    = null;
                    item._loadedTier = -1;
                    this.items.push(item);

                    x   += ITEM_W + GAP;
                    rowH = Math.max(rowH, item._h);
                }

                if (coll && (coll.name || coll.title)) {
                    const label = document.createElement('div');
                    label.className = 'gallery-dom-label';
                    label.textContent = coll.name || coll.title;
                    groupEl.appendChild(label);
                }

                this._groupEls.push(groupEl);
                const groupObj = { key, label: coll?.name || coll?.title || '', rowY: y, rowH, width: x, items: row, el: groupEl };
                for (const it of row) it._group = groupObj;
                this.groups.push(groupObj);
                this._afterGroupBuilt(groupObj);
                this.worldW = Math.max(this.worldW, x);
                y += rowH + ROW_GAP;
                world.appendChild(groupEl);
            }
            y += SECTION_GAP - ROW_GAP;
        }

        this.worldH = y > 0 ? y - SECTION_GAP : 0;
        world.style.width  = `${this.worldW}px`;
        world.style.height = `${this.worldH}px`;
        this.viewport.appendChild(world);
        this._world = world;

        this._applyInitialView(isAccessory);

        this.items.forEach((item, idx) => this.lbIndex.set(item.id, idx));
        this._keepAll = this.items.length <= KEEP_ALL_MAX;
        this._renderFilterButtons();
        this._updateScene(); // initial mount (keep-all → the whole strip at once)
    }

    // Seam: which sections buildLayout instantiates. The mobile default is ONE
    // collection at a time — we never instantiate the full ~250-item set; that
    // simultaneous-mount burst is the iOS crash. GalleryWorldview overrides this
    // to lay out the full world (its strip layer carries the far view instead).
    _sectionsForBuild(typeItems, keyFor) {
        this._buildCollectionList(typeItems, keyFor);
        const haveKeys = new Set(typeItems.map(keyFor));
        if (!this._activeCollection || !haveKeys.has(this._activeCollection)) {
            this._activeCollection = this._resolveInitialCollection(typeItems, keyFor);
        }
        this._activeFilter = this._activeCollection; // _itemVisible / _centerItem rely on this
        return [{ items: typeItems.filter(i => keyFor(i) === this._activeCollection) }];
    }

    // Build the full switcher list (every collection that has items of this
    // type, in collMap order). Shared by the scoped and world-view layouts.
    _buildCollectionList(typeItems, keyFor) {
        const haveKeys = new Set(typeItems.map(keyFor));
        this._collectionList = [...this.collMap.entries()]
            .filter(([id]) => haveKeys.has(id))
            .map(([id, c]) => ({ key: id, label: c.name || c.title || '' }));
        if (haveKeys.has('__none__')) this._collectionList.push({ key: '__none__', label: 'Other' });
    }

    // Seam: no-op hook, called once per group right after it's pushed.
    // GalleryWorldview attaches the row's strip layer here.
    _afterGroupBuilt(groupObj) {} // eslint-disable-line no-unused-vars

    // Seam: initial zoom/pan + zoom-level ladder once the world is measured.
    _applyInitialView(isAccessory) {
        const fitZ = this.worldH > 0 ? this.H / this.worldH : 1;
        // A single collection is one short row, so fill-height fitZ can exceed
        // ZOOM_MAX — clamp it so the user can still pinch back out to the start.
        this.zoom = Math.min(this.ZOOM_MAX, Math.max(this.ZOOM_MIN, isAccessory ? fitZ * 0.75 : fitZ));
        if (this.groups.length <= 1) {
            // Single horizontal row: centre it vertically, start at the first look.
            this.panX = CLAMP_MIN_PX;
            this.panY = (this.H - this.worldH * this.zoom) / 2;
        } else {
            this.panX = isAccessory ? 80 : 0;
            this.panY = isAccessory ? 80 : 0;
        }
        this.fitZ = fitZ;
        this.labelFullZ = Math.min(fitZ * 4, this.ZOOM_MAX);
        this.ZOOM_LEVELS = [
            Math.max(fitZ * 0.25, this.ZOOM_MIN),
            Math.max(fitZ * 0.5,  this.ZOOM_MIN),
            fitZ,
            this.labelFullZ,
            Math.min(fitZ * 10, this.ZOOM_MAX),
            this.ZOOM_MAX,
        ].sort((a, b) => a - b).filter((z, i, a) => i === 0 || z > a[i - 1] * 1.2);
    }

    // Resolve which collection to show first: explicit ?filter=, else the
    // collection of a deep-linked ?item=, else the first collection in order.
    _resolveInitialCollection(typeItems, keyFor) {
        const params      = new URLSearchParams(location.search);
        const filterParam = params.get('filter');
        if (filterParam && filterParam !== 'all') {
            const want = filterParam.toLowerCase();
            for (const [id, c] of this.collMap) {
                if ((c.name || c.title || '').toLowerCase() === want) return id;
            }
        }
        const itemParam = params.get('item')
            || new URLSearchParams(location.hash.slice(1)).get('item');
        if (itemParam) {
            const it = typeItems.find(i =>
                i.id === itemParam || this.titleToSlug(i.title || '') === itemParam);
            if (it) return keyFor(it);
        }
        return this._collectionList[0]?.key || '__none__';
    }

    // ── Mount / unmount ───────────────────────────────────────────────────────
    // Items are full <img> tags only while in the viewport (+ small margin).
    // Off-viewport items have NO DOM, NO src, NO decoded image data — the
    // browser releases all of it on .remove(). This is what bounds memory on
    // iOS where decoded image data lingers far longer than we'd like.
    //
    // Mount cost is small: create one <img> + (optional) frame, append to a
    // group. The <img> starts with a bh-tint background-color so any brief
    // window before the src loads doesn't flash the page background through.

    _bhTintFor(item) {
        if (item._bhTint !== undefined) return item._bhTint;
        if (item.img_blurhash) {
            try {
                const px = bhDecode(item.img_blurhash, 1, 1);
                item._bhTint = `rgb(${px[0]},${px[1]},${px[2]})`;
            } catch { item._bhTint = '#444'; }
        } else {
            item._bhTint = '#444';
        }
        return item._bhTint;
    }

    // Horizontal intersect with the (unbuffered) viewport. The single-collection
    // view is one row, so vertical always intersects — X is what matters.
    _isOnScreen(item) {
        const vl = -this.panX / this.zoom;
        const vr = vl + this.W / this.zoom;
        return item._x < vr && item._x + ITEM_W > vl;
    }

    _mountItem(item) {
        if (item._el) return;
        const groupEl = item._group?.el;
        if (!groupEl) return;

        // Frame for accessory items (white background like Pixi version).
        if (item._dw !== ITEM_W || item._dh !== item._h) {
            const frame = document.createElement('div');
            frame.className = 'gallery-dom-frame';
            frame.style.cssText = `left:${item._x}px;top:0;width:${ITEM_W}px;height:${item._h}px;`;
            if (!this._itemVisible(item)) frame.style.display = 'none';
            groupEl.appendChild(frame);
            item._frameEl = frame;
        }

        // Wrapper div + TWO <img> layers. Tier swaps load the new src on the
        // hidden layer; once it's decoded we toggle which layer is visible.
        // The old image stays on screen the entire time — no flash.
        const tint    = this._bhTintFor(item);
        const wrapper = document.createElement('div');
        wrapper.className = 'gallery-dom-item';
        wrapper.style.cssText = `left:${item._x + item._dx}px;top:${item._dy}px;width:${item._dw}px;height:${item._dh}px;background-color:${tint};`;
        wrapper.dataset.id = item.id;
        if (!this._itemVisible(item)) wrapper.style.display = 'none';

        const mkImg = isCurrent => {
            const img = document.createElement('img');
            img.className = `gallery-dom-img${isCurrent ? ' is-current' : ''}`;
            img.decoding  = 'async';
            img.draggable = false;
            return img;
        };
        const imgA = mkImg(true);   // visible
        const imgB = mkImg(false);  // hidden (preload target)
        wrapper.append(imgA, imgB);

        // On-screen looks paint straight at tier 1 (300px) for a decent first
        // frame; off-screen looks start at tier 0 (60px) and upgrade lazily.
        const initTier = this._isOnScreen(item) ? 1 : 0;
        const url = this.thumbUrl(item, initTier);
        if (url) { imgA.src = url; item._loadedTier = initTier; }

        groupEl.appendChild(wrapper);
        item._el     = wrapper;
        item._imgA   = imgA;
        item._imgB   = imgB;
        item._curImg = imgA;

        this._mounted.add(item);
    }

    _unmountItem(item) {
        if (!item._el) return;
        item._el.remove();
        if (item._frameEl) item._frameEl.remove();
        item._el         = null;
        item._frameEl    = null;
        item._imgA       = null;
        item._imgB       = null;
        item._curImg     = null;
        item._loadedTier = -1;
        this._mounted.delete(item);
    }

    // ── Scene update ──────────────────────────────────────────────────────────
    // Per pan/zoom: mount items entering the (viewport + margin), unmount
    // items leaving it. Visible-and-mounted items below the current zoom's
    // tier get queued for an upgrade swap.
    //
    // Performance shape (mirrors the PixiJS variant):
    //   - Binary-search through groups to find the first visible row, then
    //     iterate only the rows that fall inside the buffered viewport. Items
    //     in groups above/below are never touched on this tick.
    //   - Items visited get a `_visitGen` stamp. After the loop, iterate the
    //     small _mounted Set and unmount anything that wasn't visited.
    //   - Motion-sensitive throttle: wider margin + capped mounts + skip
    //     unmounts/upgrades while pan/zoom velocity is non-trivial. Settle
    //     does the full prune + tier upgrade pass.
    _updateScene() {
        const groups = this.groups;
        if (!groups.length) return;

        // World-view mount gate (inert unless a subclass sets _mountZoomMin):
        // below the gate the strip layer carries the whole frame, so shed
        // per-item DOM (bounded per call) and skip everything else.
        if (this._mountZoomMin && this.zoom < this._mountZoomMin) {
            this._tierLoadQueue.length = 0;
            let unmounted = 0;
            for (const item of this._mounted) {
                if (unmounted >= 30) break;
                this._unmountItem(item);
                unmounted++;
            }
            return;
        }

        const baseTier = tierForZoom(this.zoom);
        const moving   = this._sceneInMotion();
        const margin     = moving ? 0.5 : 0.25;
        const maxMount   = moving ? (this._maxMountMoving ?? 4) : (this._maxMountSettled ?? 30);
        const maxUnmount = 30; // bound the settle sweep too — iOS chokes on big bursts

        const vl = -this.panX / this.zoom;
        const vt = -this.panY / this.zoom;
        const vr = vl + this.W / this.zoom;
        const vb = vt + this.H / this.zoom;
        const marginX = this.W / this.zoom * margin;
        const marginY = this.H / this.zoom * margin;

        // gvy(g): visual top of group g, including spotlight-gap offset.
        const gvy = g => g.rowY + this._groupOffset(g);

        // Binary search: first group whose visual bottom falls inside the
        // top-of-margin. Skip everything above.
        let lo = 0, hi = groups.length - 1, firstGi = groups.length;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (gvy(groups[mid]) + groups[mid].rowH > vt - marginY) { firstGi = mid; hi = mid - 1; }
            else lo = mid + 1;
        }

        // Discard any pending stale-tier requests; in-flight ones complete
        // and re-trigger the drain on settle.
        this._tierLoadQueue.length = 0;

        this._gen = (this._gen || 0) + 1;
        const gen = this._gen;
        const keepAll = this._keepAll;
        let mounted = 0;
        let pendingMount = false;
        for (let gi = firstGi; gi < groups.length; gi++) {
            const g = groups[gi];
            const vy = gvy(g);
            if (vy > vb + marginY) break; // past the bottom-of-margin
            for (const item of g.items) {
                const farOff = item._x >= vr + marginX || item._x + ITEM_W <= vl - marginX;
                // Keep-all mounts the whole strip; windowed mode culls far items.
                if (farOff && !keepAll) continue;
                if (!this._itemVisible(item)) continue;
                item._visitGen = gen;
                if (!item._el) {
                    if (!keepAll && mounted >= maxMount) { pendingMount = true; continue; }
                    // Hard safety valve (subclass-only): never exceed an
                    // absolute mounted-item count, whatever the viewport says.
                    if (this._mountedMax && this._mounted.size >= this._mountedMax) continue;
                    this._mountItem(item);
                    mounted++;
                }
                if (moving) continue;
                // Tier target: on-screen looks may reach the zoom's full tier
                // (e.g. 800px at fit); off-screen looks cap at 300px. Keep-all
                // also pulls far-off looks back down to 300px to reclaim their
                // ~3.8MB 800px bitmaps — so only on-screen looks ever hold 800px.
                const onscreen = item._x < vr && item._x + ITEM_W > vl;
                const target   = onscreen ? baseTier : Math.min(baseTier, 1);
                if (item._loadedTier < target) {
                    const url = this.thumbUrl(item, target);
                    if (url) this._tierLoadQueue.push({ item, tier: target, url });
                } else if (keepAll && farOff && item._loadedTier > 1) {
                    const url = this.thumbUrl(item, 1);
                    if (url) this._tierLoadQueue.push({ item, tier: 1, url });
                }
            }
        }

        // Unmount sweep: windowed mode only. Keep-all never unmounts, so a look
        // scrolled past keeps its loaded tier and never reloads on return.
        if (!moving && !keepAll) {
            let unmounted = 0;
            for (const item of this._mounted) {
                if (item._visitGen === gen) continue;
                if (unmounted >= maxUnmount) break;
                this._unmountItem(item);
                unmounted++;
            }
        }

        if (moving) return; // skip sort/drain — we deferred queueing

        // Sort upgrades by viewport-distance — closest to centre first.
        const cx = (this.W / 2 - this.panX) / this.zoom;
        const cy = (this.H / 2 - this.panY) / this.zoom;
        this._tierLoadQueue.sort((a, b) => {
            const da = Math.abs(a.item._x - cx) + Math.abs(this._itemVisualY(a.item) - cy);
            const db = Math.abs(b.item._x - cx) + Math.abs(this._itemVisualY(b.item) - cy);
            return da - db;
        });

        this._drainTierLoadQueue();

        // World view uses a small settled mount batch. Continue filling it
        // without requiring another pan/zoom event; the stable path leaves
        // _maxMountSettled unset and retains its existing one-pass behaviour.
        if (pendingMount && this._maxMountSettled && !this._sceneFillTimer) {
            this._sceneFillTimer = setTimeout(() => {
                this._sceneFillTimer = null;
                if (!this._destroyed) this._updateScene();
            }, 32);
        }
    }

    _sceneInMotion() {
        return this._dragging
            || this._pinching
            || this._zoomAnimating
            || Math.abs(this.velX)    > 0.3
            || Math.abs(this.velY)    > 0.3
            || Math.abs(this.zoomVel) > 0.001;
    }

    _drainTierLoadQueue() {
        if (this._destroyed) return;
        while (this._tierLoadActive < TIER_LOAD_MAX && this._tierLoadQueue.length) {
            const { item, tier, url } = this._tierLoadQueue.shift();
            // Skip stale: item no longer mounted, or already at this tier or higher.
            if (!item._el || item._loadedTier === tier) continue;
            // Load on the OFF img layer (the one currently hidden). The visible
            // layer stays on screen until the off layer is fully decoded — then
            // we toggle .is-current and CSS crossfades opacity.
            const offImg = item._curImg === item._imgA ? item._imgB : item._imgA;
            if (!offImg) continue;
            this._tierLoadActive++;
            const finish = () => {
                this._tierLoadActive--;
                this._drainTierLoadQueue();
            };
            const swap = () => {
                if (item._el && item._loadedTier !== tier) {
                    const old = item._curImg; // the now-superseded layer
                    offImg.classList.add('is-current');
                    if (old) old.classList.remove('is-current');
                    item._curImg = offImg;
                    item._loadedTier = tier;
                    // The spare layer now holds a bitmap we'll never show again
                    // (tiers swap up on focus and back down off-screen) — drop its
                    // src to free the decoded image. It's invisible (opacity 0),
                    // so no flash, and it's reused as the target on the next swap.
                    if (old && old !== offImg) {
                        // decode() can resolve before Chromium presents the new
                        // bitmap. Keep the old layer through the next paint so
                        // Android cannot expose a one-frame transparent hole.
                        const staleSrc = old.getAttribute('src');
                        requestAnimationFrame(() => requestAnimationFrame(() => {
                            if (item._curImg !== old && old.getAttribute('src') === staleSrc) {
                                old.removeAttribute('src');
                            }
                        }));
                    }
                }
                finish();
            };
            offImg.src = url;
            if (offImg.decode) {
                offImg.decode().then(swap).catch(finish);
            } else {
                offImg.onload  = swap;
                offImg.onerror = finish;
            }
        }
    }

    // ── Filter ────────────────────────────────────────────────────────────────

    _itemVisible(item) {
        return this._activeFilter === 'all' || item._collKey === this._activeFilter;
    }

    // Mobile filter = collection switcher. Rebuild the layout scoped to the new
    // collection rather than CSS-hiding rows — the single-collection path keeps
    // the instantiated item count bounded (~25), which is the whole point.
    setFilter(key) {
        if (!key || key === 'all') key = this._collectionList[0]?.key;
        if (!key || key === this._activeCollection) return;
        this._activeCollection = key;
        this.buildLayout();   // re-scopes to _activeCollection, re-renders buttons
        resetCrumbTracking(this);
        this._syncWorld();
        this._wake();
        const label = this._collectionList.find(c => c.key === key)?.label;
        if (label) {
            history.replaceState(null, '', `${location.pathname}?filter=${label.toLowerCase()}`);
        }
    }

    _renderFilterButtons() {
        if (this._collectionList.length < 2) return;
        const nav = document.getElementById('gallery-filter');
        renderFilterButtons(nav, this._collectionList, this._activeCollection, key => {
            this.setFilter(key);
        }, { includeAll: false });
    }

    // ── URLs ──────────────────────────────────────────────────────────────────

    thumbUrl(item, tierIdx) {
        if (!item.file || !item.collectionId || !item.id) return null;
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.file}?thumb=${TIERS[tierIdx]}`;
    }

    fullUrl(item) {
        if (!item.file || !item.collectionId || !item.id) return '';
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.file}`;
    }

    bestThumbUrl(item) {
        return this.thumbUrl(item, Math.max(0, item._loadedTier ?? 1));
    }

    // ── RAF loop / sync ───────────────────────────────────────────────────────

    _tick(now = performance.now()) {
        const dt = Math.min(now - (this._lastTick ?? now), 64);
        this._lastTick = now;
        applyInertia(this, dt);

        // Include motion state so releasing a drag/pinch forces one settled
        // scene pass even when the transform itself did not change that frame.
        const panKey = `${this.panX.toFixed(1)},${this.panY.toFixed(1)},${this.zoom.toFixed(3)},${this._sceneInMotion() ? 1 : 0}`;
        if (panKey !== this._lastPanKey || (this._crumbTrackingStarted && !this._crumbVisible)) {
            this._lastPanKey = panKey;
            const center = this._centerItem();
            if (!this._crumbInitialised) {
                this._crumbItem = center;
                this._crumbInitialised = true;
            }
            if (this._crumbTrackingStarted && (!this._crumbVisible || center !== this._crumbItem)) {
                this._crumbItem = center;
                this.updateCrumb(center);
                this._crumbVisible = true;
            }
            this._updateScene();
        }

        const idle = !this._dragging
            && !this._pinching
            && !this._zoomAnimating
            && Math.abs(this.velX)    < 0.01
            && Math.abs(this.velY)    < 0.01
            && Math.abs(this.zoomVel) < 0.0005;
        this._raf = idle ? null : requestAnimationFrame(t => this._tick(t));
    }

    _wake() {
        if (this._raf === null) {
            this._raf = requestAnimationFrame(t => this._tick(t));
        }
    }

    _maybeIdle() {
        if (Math.abs(this.velX) < 0.001 && Math.abs(this.velY) < 0.001 &&
            Math.abs(this.zoomVel) < 0.0005 && !this._dragging &&
            !this._pinching && !this._zoomAnimating) {
            this._raf = null;
        }
    }

    bindEvents() {
        bindPointerEvents(this, this.viewport, { hasCursor: false, trackPinchStartZ: true });
        document.getElementById('zoom-in') ?.addEventListener('click', () => this.zoomIn());
        document.getElementById('zoom-out')?.addEventListener('click', () => this.zoomOut());
    }

    // ── Zoom / pan ────────────────────────────────────────────────────────────

    zoomAt(cx, cy, factor) {
        const newZoom = Math.max(this.ZOOM_MIN, Math.min(this.ZOOM_MAX, this.zoom * factor));
        const ratio   = newZoom / this.zoom;
        this.panX = cx - ratio * (cx - this.panX);
        this.panY = cy - ratio * (cy - this.panY);
        this.zoom = newZoom;
        this._syncWorld();
    }

    // Spotlight gap: per-group Y offset that fades in from 0 at fit zoom to
    // full at labelFullZ. Sigmoid centred on the screen's world-y so only the
    // row closest to centre gets the breathing room; others ease toward base.
    _groupOffset(g) {
        const fitZ      = this.fitZ ?? this.zoom;
        const targetZ   = this.labelFullZ ?? fitZ * 4;
        const zoomScale = Math.max(0, Math.min(1, (this.zoom - fitZ) / (targetZ - fitZ)));
        if (zoomScale === 0) return 0;
        const focusCenterY = (this.H / 2 - this.panY) / this.zoom;
        const groupCenterY = g.rowY + g.rowH / 2;
        const x = (groupCenterY - focusCenterY) / ROW_GAP_SOFTNESS;
        const s = 1 / (1 + Math.exp(-x));
        return ROW_GAP_FOCUS * zoomScale * (s - 0.5);
    }

    _syncWorld() {
        if (!this._world) return;
        this.clamp();
        // CSS transform: translate then scale. iOS GPU-composites this cheaply.
        // translate3d hint forces a compositor layer.
        this._world.style.transform =
            `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.zoom})`;

        // Spotlight gap: shift each group container by translateY (composite
        // only, no reflow — group `top` stays at the original rowY).
        for (const g of this.groups) {
            const off = this._groupOffset(g);
            // Track last value so we don't write the same string every frame.
            if (g._lastOff === off) continue;
            g._lastOff = off;
            g.el.style.transform = off ? `translateY(${off}px)` : '';
        }
    }

    clamp() {
        const ww = this.worldW * this.zoom;
        const wh = this.worldH * this.zoom;
        this.panX = Math.max(CLAMP_MIN_PX - ww, Math.min(this.W - CLAMP_MIN_PX, this.panX));

        // Single-collection view: lock Y to the row's vertical centre and scroll
        // X only — identical to the desktop canvas when a collection is filtered
        // (its clamp() activeGroup branch). A sideways swipe never drifts Y.
        if (this.groups.length < 2) {
            this.panY = (this.H - wh) / 2;
            this.velY = 0;
            return;
        }

        // Filtered to one collection while the rest of the world is still laid
        // out (the world view): lock Y to that row and bound X to its own width,
        // exactly as the desktop canvas does. Without this you could pan up into
        // the empty space where the hidden rows still sit.
        if (this._activeFilter !== 'all') {
            const g = this.groups.find(gr => gr.key === this._activeFilter);
            if (g) {
                this.panY = this.H / 2 - (g.rowY + g.rowH / 2) * this.zoom;
                this.velY = 0;
                const lockedW = g.width * this.zoom;
                this.panX = Math.max(CLAMP_MIN_PX - lockedW, Math.min(this.W - CLAMP_MIN_PX, this.panX));
                return;
            }
        }

        this.panY = Math.max(CLAMP_MIN_PX - wh, Math.min(this.H - CLAMP_MIN_PX, this.panY));

        // Sticky-row Y-lock when zoomed in — see STICKY_AT for the thresholds.
        // At/above stickyZ it also re-clamps X to the locked row's width.
        if (!STICKY_ROWS || this.groups.length < 2) return;
        const stickyZ = Math.min(this.fitZ * STICKY_AT, this.ZOOM_MAX);
        if (!stickyZ || this.zoom <= stickyZ * STICKY_SOFT) return;

        const viewCY = (this.H / 2 - this.panY) / this.zoom;
        let nearest = this.groups[0], bestDist = Infinity;
        for (const g of this.groups) {
            const d = Math.abs(g.rowY + g.rowH / 2 - viewCY);
            if (d < bestDist) { bestDist = d; nearest = g; }
        }
        const targetY = this.H / 2 - (nearest.rowY + nearest.rowH / 2) * this.zoom;

        if (this.zoom >= stickyZ) {
            this.panY = targetY;
            this.velY = 0;
            const lockedW = nearest.width * this.zoom;
            this.panX = Math.max(CLAMP_MIN_PX - lockedW, Math.min(this.W - CLAMP_MIN_PX, this.panX));
        } else if (!this._dragging && Math.abs(this.velY) < STICKY_ESCAPE) {
            this.panY += (targetY - this.panY) * STICKY_STRENGTH;
            this.velY *= (1 - STICKY_STRENGTH);
        }
    }

    zoomIn()  { stepZoom(this,  1); }
    zoomOut() { stepZoom(this, -1); }

    animateZoomCenter(targetZ, onComplete) {
        this.animateZoom(targetZ, this.W / 2, this.H / 2, onComplete);
    }

    animateZoom(targetZ, screenCX, screenCY, onComplete) {
        this.zoomVel = 0;
        this._zoomAnimating = true;
        this._wake();
        const startZ    = this.zoom;
        const startTime = performance.now();
        const tick = now => {
            const t     = Math.min(1, (now - startTime) / 300);
            const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoomAt(screenCX, screenCY, (startZ + (targetZ - startZ) * eased) / this.zoom);
            this._wake();
            if (t < 1) requestAnimationFrame(tick);
            else {
                this._zoomAnimating = false;
                this._lastPanKey = '';
                this._wake();
                this._maybeIdle();
                onComplete?.();
            }
        };
        requestAnimationFrame(tick);
    }

    // ── Click / cursor ────────────────────────────────────────────────────────

    setCursor() { /* no-op on touch */ }

    handleClick(sx, sy) {
        const item = this.itemAt(sx, sy);
        if (!item) return;
        const lbThreshold = ZOOM_LIGHTBOX * 0.5;
        if (this.zoom >= lbThreshold) {
            this.openLightbox(item.id);
        } else {
            const screenCX = (item._x + ITEM_W / 2) * this.zoom + this.panX;
            const screenCY = (this._itemVisualY(item) + item._h / 2) * this.zoom + this.panY;
            this.animateZoom(this.zoom * 2.5, screenCX, screenCY);
        }
    }

    _itemVisualY(item) {
        return item._y + (item._group ? this._groupOffset(item._group) : 0);
    }

    _centerItem() {
        const cx = (this.W / 2 - this.panX) / this.zoom;
        const cy = (this.H / 2 - this.panY) / this.zoom;
        let best = null, bestDist = Infinity;
        for (const item of this.items) {
            if (!this._itemVisible(item)) continue;
            const vy = this._itemVisualY(item);
            const dx = (item._x + ITEM_W / 2) - cx;
            const dy = (vy + item._h / 2) - cy;
            const d  = dx * dx + dy * dy;
            if (d < bestDist) { bestDist = d; best = item; }
        }
        return best;
    }

    itemAt(sx, sy) {
        const wx = (sx - this.panX) / this.zoom;
        const wy = (sy - this.panY) / this.zoom;
        for (const item of this.items) {
            if (!this._itemVisible(item)) continue;
            const vy = this._itemVisualY(item);
            if (wx >= item._x && wx <= item._x + ITEM_W &&
                wy >= vy && wy <= vy + item._h) return item;
        }
        return null;
    }

    // ── Lightbox ──────────────────────────────────────────────────────────────

    async openLightbox(id, pool = this.items) {
        const index = pool.findIndex(item => item.id === id);
        if (index < 0) return;

        const vpRect = this.viewport.getBoundingClientRect();

        const ARROW_PREV = '<div class="pswp-arrow"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="square" stroke-linejoin="square" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"/></svg></div>';
        const ARROW_NEXT = '<div class="pswp-arrow"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="square" stroke-linejoin="square" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg></div>';

        const pswp = new PhotoSwipe({
            trapFocus:    false,
            returnFocus:  false,
            arrowPrevSVG: ARROW_PREV,
            arrowNextSVG: ARROW_NEXT,
            closeSVG: '<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none"><path d="M0.353546 0.353546L17.8535 17.8535" stroke="white"/><path d="M17.8535 0.353546L0.353546 17.8535" stroke="white"/></svg>',
            zoomSVG: '<span class="inline-block">−</span><span class="inline-block">+</span>',
            dataSource: pool.map(item => ({
                src:    this.fullUrl(item),
                msrc:   this.bestThumbUrl(item),
                width:  item.img_w || ITEM_W * 4,
                height: item.img_h || ITEM_H * 4,
            })),
            index,
            bgOpacity:          0.95,
            secondaryZoomLevel: 1,
            maxZoomLevel:       4,
            getThumbBoundsFn: (i) => {
                const it = pool[i];
                if (!it) return null;
                return {
                    x: it._x * this.zoom + this.panX + vpRect.left,
                    y: this._itemVisualY(it) * this.zoom + this.panY + vpRect.top,
                    w: ITEM_W * this.zoom,
                };
            },
        });

        const onSlide = () => {
            const c = pswp.currIndex;
            const item = pool[c];
            if (item) {
                const slug = this.titleToSlug(item.title);
                history.replaceState(null, '', '#item=' + slug);
                this.updateCrumb(item);
                const shareUrl = `${location.origin}${location.pathname}?item=${slug}`;
                showShare(item, shareUrl);
            }
        };
        pswp.on('change', onSlide);
        pswp.on('openingAnimationEnd', onSlide);

        pswp.on('beforeOpen', () => document.body.classList.add('lightbox-open'));
        pswp.on('close', () => {
            document.body.classList.remove('lightbox-open');
            history.replaceState(null, '', window.location.pathname + window.location.search);
            this.updateCrumb(this._crumbItem);
            hideShare();
        });

        pswp.on('loadComplete', e => {
            const slide = e.slide;
            const img   = slide.content.element;
            if (img && img.naturalWidth) {
                slide.data.width  = img.naturalWidth;
                slide.data.height = img.naturalHeight;
                slide.updateContentSize(true);
            }
        });

        pswp.init();
    }

    // ── URL param ─────────────────────────────────────────────────────────────

    titleToSlug(title) {
        return title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }

    checkUrlParam() {
        // ?filter= is already applied at build time (single-collection scoping),
        // so only the ?item= deep-link needs handling here.
        const params    = new URLSearchParams(window.location.search);
        const itemParam = params.get('item')
            || new URLSearchParams(window.location.hash.slice(1)).get('item');
        if (itemParam) {
            const item = this.items.find(i => i.id === itemParam || this.titleToSlug(i.title) === itemParam);
            if (item) this.navigateTo(item.id, true, false);
        }
    }

    navigateTo(id, openLb = true, animate = true) {
        const item = this.items.find(i => i.id === id);
        if (!item) return;

        const targetZ = Math.max(this.zoom, this.labelFullZ ?? ZOOM_LIGHTBOX * 0.5);
        const endX    = this.W / 2 - (item._x + ITEM_W / 2) * targetZ;
        const endY    = this.H / 2 - (item._y + item._h / 2) * targetZ;

        this.zoomVel = 0;
        this.velX = this.velY = 0;

        if (!animate) {
            this.zoom = targetZ;
            this.panX = endX;
            this.panY = endY;
            this._syncWorld();
            this._wake();
            if (openLb) requestAnimationFrame(() => this.openLightbox(id));
            return;
        }

        this._zoomAnimating = true;
        const startZ  = this.zoom;
        const startX  = this.panX;
        const startY  = this.panY;
        const startTime = performance.now();
        const tick = now => {
            const t = Math.min(1, (now - startTime) / 500);
            const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoom = startZ + (targetZ - startZ) * e;
            this.panX = startX + (endX - startX) * e;
            this.panY = startY + (endY - startY) * e;
            this._syncWorld();
            this._wake();
            if (t < 1) requestAnimationFrame(tick);
            else {
                this._zoomAnimating = false;
                this._lastPanKey = '';
                this._wake();
                if (openLb) setTimeout(() => this.openLightbox(id), 200);
            }
        };
        requestAnimationFrame(tick);
    }

    // ── Breadcrumb ────────────────────────────────────────────────────────────

    updateCrumb(item) {
        const el = document.getElementById('gallery-crumb');
        if (!el) return;

        // Separator spacing: see the note in gallery-animate-zoom.js updateCrumb.
        // The &nbsp; must be the only space, so no whitespace between tags.
        const sep  = '<span class="hidden md:inline">&nbsp;/ </span>';
        const part = text => `<span class="block md:inline">${text}</span>`;

        if (!item) {
            const label = this._collectionList.find(c => c.key === this._activeFilter)?.label;
            el.innerHTML = label ? sep + part(label) : '';
            return;
        }

        const m = (item.title || '').match(/^([A-Z]{2}\d{2})\s+(.+)$/);
        el.innerHTML = m
            ? sep + part(m[1]) + sep + part(m[2])
            : sep + part(item.title);
    }
}

export { GalleryAnimateZoomDOM };
